'use client'
import { ChevronsRight } from 'lucide-react'
import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import { CMSLink } from '@/components/Link'
import type { Heroblock4 } from '@/payload-types'
import { FormBlock2 } from '@/blocks/Form/Component2'
import { cn } from '@/utilities/ui'

const Heroblock4 = ({
  eyebrow,
  title,
  heading,
  description,
  hasPrimaryButton,
  primaryButton,
  hasSecondaryButton,
  secondaryButton,
  BackgroundImage,
  hasForm,
  form,
}: Heroblock4) => {
  return (
    <section className="relative font-sans overflow-hidden" aria-label="Hero Section">
      {/* Background Image - Optimized for LCP */}
      {BackgroundImage && typeof BackgroundImage === 'object' && BackgroundImage.url && (
        <div className="absolute left-0 top-0 w-full h-full z-0">
          <Media
            resource={BackgroundImage}
            alt={BackgroundImage.alt || 'Hero Background'}
            imgClassName="object-cover blur"
            style={{ maxWidth: '100vw', minHeight: '100vh', left: 0, top: 0 }}
            priority={true}
            fill={true}
            sizes="(max-width: 640px) 640px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, (max-width: 1280px) 1280px, (max-width: 1440px) 1409px, 1920px"
            quality={40}
            fetchPriority="high"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
      )}

      {/* Main Content Container - flip order on mobile */}
      <div className="container mx-auto w-full 2xl:px-0 pt-32 md:pt-36 lg:pt-36 xl:pt-40 mb-16 relative z-10 flex flex-col-reverse lg:flex-row items-start lg:items-start gap-4 lg:gap-8">
        {/* Hero Text Section */}
        <section className="w-full lg:flex-1 flex flex-col lg:justify-center space-y-4">
          {eyebrow && (
            <div
              className="border border-primary-foreground px-4 py-1 text-xs font-medium rounded-full w-fit bg-primary/5"
              role="banner"
            >
              <span className="text-primary-foreground font-medium">{eyebrow}</span>
            </div>
          )}
          {heading && (
            <div className="text-primary-foreground text-left">
              <RichText
                data={heading}
                enableGutter={false}
                enableProse={false}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-left mb-6 lg:max-w-3xl"
              />
            </div>
          )}
          {description && (
            <div className="text-primary-foreground w-full max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl">
              <RichText
                data={description}
                enableGutter={false}
                enableProse={false}
                className="text-lg md:text-xl text-primary-foreground max-w-3xl"
              />
            </div>
          )}
          {(hasPrimaryButton || hasSecondaryButton) && (
            <div className="flex flex-wrap gap-4 pt-2 justify-start items-start">
              {hasPrimaryButton && primaryButton && (
                <CMSLink
                  {...primaryButton}
                  className={cn(
                    `inline-flex items-center border border-primary text-primary-foreground cursor-pointer hover:text-primary-foreground font-semibold text-sm sm:text-base tracking-wide transition-colors duration-300 group/link rounded-full px-4 sm:px-6 py-6 sm:py-8 w-auto`,
                  )}
                >
                  <span className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center ml-2 group-hover/link:bg-primary">
                    <ChevronsRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                </CMSLink>
              )}
              {hasSecondaryButton && secondaryButton && (
                <CMSLink
                  {...secondaryButton}
                  className={cn(
                    `inline-flex items-center border bg-secondary-foreground border-secondary-foreground text-secondary cursor-pointer hover:bg-secondary hover:text-secondary-foreground font-semibold text-sm sm:text-base tracking-wide transition-colors duration-300 group/link rounded-full px-4 sm:px-6 py-6 sm:py-8 w-auto group-hover/link:bg-secondary group-hover/link:text-secondary-foreground`,
                  )}
                >
                  <span className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary rounded-full flex items-center justify-center ml-2 group-hover/link:bg-secondary">
                    <ChevronsRight className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-foreground" />
                  </span>
                </CMSLink>
              )}
            </div>
          )}
        </section>
        {/* Form Section */}
        {hasForm && form && (
          <div className="w-full lg:w-1/2 overflow-hidden flex items-start justify-center xl:justify-end">
            {/* Custom form with title inside FormBlock2 wrapper */}
            <div className="w-full max-w-2xl min-w-[400px]">
              {/* FormBlock2 with custom styling and title passed as a prop */}
              <FormBlock2
                form={
                  typeof form === 'object' && form !== null && 'value' in form
                    ? (form as any).value
                    : form
                }
                enableIntro={false}
                hideImageSection={true}
                bareForm={true}
                title={title || undefined} // Ensure title is undefined if null
              />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default Heroblock4
